/* eslint-disable no-restricted-syntax */
import shortid from 'shortid';
import isURL from 'validator/es/lib/isURL';

import { SET_MODAL_TYPE } from '../../actionConstants';
import { showProgressModal } from '../Exports/actions';
import { setProgressValue, showInfoModal } from '../Modal/actions';

import { formatDate, getUserTimezone, loadUser } from '../../utils';
import { getFullName } from '../Person/utils';
import { getHighPriorityRoleName } from '../User/utils';

import { ABORT_REASONS, SAFE_PDF_RECORDS_NUMBER } from './constants';

import { CONFIRM } from '../Modal/modalTypes';

async function* fetchData(dispatch, getItems, isLimitedPDF, id, controller) {
  if (dispatch && getItems) {
    let nextItems = { limit: 50, offset: 0 };

    do {
      const response = await getItems({ ...nextItems, controller });

      if (
        typeof response !== 'object' ||
        !response?.data ||
        !Array.isArray(response.data?.results)
      ) {
        throw new Error();
      }

      const { count, next, results } = response.data;
      nextItems = next;
      if (isLimitedPDF && next?.offset >= SAFE_PDF_RECORDS_NUMBER) {
        nextItems = null;
      }
      const progressValue = nextItems?.offset
        ? Math.round((100 / count) * nextItems.offset)
        : 100;

      setProgressValue(dispatch, progressValue, id);
      yield results;
    } while (nextItems);
  }
}

export const getExportData = async (dispatch, getItems, isLimitedPDF) => {
  const id = shortid.generate();
  const controller = new AbortController();

  const customModalClose = () => {
    controller.abort(ABORT_REASONS.MANUALLY);
  };

  showProgressModal(dispatch, true, id, customModalClose);

  let hasError = false;
  const values = [];
  try {
    for await (const items of fetchData(
      dispatch,
      getItems,
      isLimitedPDF,
      id,
      controller,
    )) {
      items?.length && values.push(...items);
    }
  } catch (error) {
    values.length = 0;
    hasError = true;
  }

  let message =
    values.length > 0
      ? `Export completed. Please wait for the file download`
      : `No data to export`;

  if (hasError) {
    message = controller?.signal?.reason
      ? controller.signal.reason
      : ABORT_REASONS.SERVER;
  }

  showProgressModal(dispatch, false);
  showInfoModal(dispatch, message);

  return values;
};

export const getItemsCount = async (getItems) => {
  const response = await getItems({ limit: 10, offset: 0 });
  return response?.data?.count;
};

export const confirmLimitedExport = (dispatch, onExport) => {
  const modalProps = {
    bodyText: `Note - only the first ${SAFE_PDF_RECORDS_NUMBER.toLocaleString()} records can currently be included in a PDF export`,
    confirmText: 'Export',
    cancelText: 'Cancel',
    onConfirm: onExport,
  };
  return dispatch({
    type: SET_MODAL_TYPE,
    modalType: CONFIRM,
    modalProps,
  });
};

export const getTablePDFSubtitle = () => {
  const cu = loadUser();
  if (!cu) return null;
  const { pmcRoles = [], properties, isAdmin } = cu;
  const isMasterPm = pmcRoles?.some(({ primary }) => primary === true);

  // get all cu roles ids (unique)
  const allUserRolesIds = [
    ...pmcRoles.map(({ role }) => role).flat(),
    ...properties.map(({ roles }) => roles).flat(),
  ].filter((roleId) => roleId);

  const highPriorityRoleName = getHighPriorityRoleName(allUserRolesIds);

  const userName = getFullName(cu);
  const roleName = isAdmin
    ? 'Admin'
    : isMasterPm
    ? 'Master PM'
    : highPriorityRoleName;

  const exportTime = formatDate(Date.now(), true, 'MM/DD/YY [at] h:mm A');
  const currentTimezone = getUserTimezone();
  return `Exported by ${userName} - ${roleName} on ${exportTime} ${currentTimezone}`;
};

export const getExportTitle = (tableTitle, pmc, building) => {
  let propertyName = null;
  if (pmc) {
    propertyName = pmc?.name;
  }
  if (building) {
    propertyName = building?.name;
  }
  return propertyName ? `${propertyName} ${tableTitle}` : tableTitle;
};

export const formatTableData = ({ headers, rows }) => [headers, ...rows];

const getOnePageLimit = () =>
  // TODO add different cases but it's better to have a limit on the API side
  1024;
const onFormatCell = (cell, onePageLimit) => {
  if (!cell || !cell.length) return '';

  if (isURL(cell)) return cell;

  const cellWithNoBreaks = cell?.replace(/[\r\n]{1,}/g, ' ');
  const words = cellWithNoBreaks.split(' ');
  const safeWordLength = 16;

  const safeWords = words
    .map((word) => {
      if (word?.length > safeWordLength) {
        const wordParts = [];
        for (let i = 0; i < word.length; i += safeWordLength) {
          wordParts.push(word.slice(i, i + safeWordLength));
        }
        return wordParts.join(' ');
      }
      return word;
    })
    .join(' ');

  if (safeWords.length > onePageLimit) {
    return safeWords.substring(0, onePageLimit);
  }
  return safeWords;
};

const onFormatRow = (row, onePageLimit) => {
  if (!row || !row.length) return null;
  return row.map((cell) => onFormatCell(cell, onePageLimit));
};

export const onFormatRows = (rows) => {
  if (!rows || !rows.length) return null;
  const onePageLimit = getOnePageLimit(rows);
  return rows.map((row) => onFormatRow(row, onePageLimit));
};
